import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from 'react-query'
import { productsAPI } from '../../services/productsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, FileText } from 'lucide-react'
import toast from 'react-hot-toast'

export default function CreateConfigModal({ isOpen, onClose, productId }) {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      product_type: '',
      api_key: '',
      api_endpoint: '',
      http_method: 'POST',
      payload: ''
    }
  })

  // Watch http_method to conditionally show payload field
  const httpMethod = watch('http_method')

  const mutation = useMutation((configData) => productsAPI.createConfig(productId, configData), {
    onSuccess: () => {
      queryClient.invalidateQueries(['product', productId])
      queryClient.invalidateQueries(['product-config', productId])
      queryClient.invalidateQueries(['product-configs', productId])
      toast.success('Config created successfully!')
      reset()
      onClose()
    },
    onError: (error) => {
      console.error('Error creating config:', error)
      toast.error(error.response?.data?.message || 'Failed to create config')
    }
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      const configData = {
        product_type: data.product_type,
        api_key: data.api_key,
        api_endpoint: data.api_endpoint,
        http_method: data.http_method,
        payload: data.http_method === 'POST' ? (data.payload || null) : null
      }
      console.log('Creating config with data:', configData)
      await mutation.mutateAsync(configData)
    } catch (error) {
      // Error handled in mutation onError
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset({
        product_type: '',
        api_key: '',
        api_endpoint: '',
        http_method: 'POST',
        payload: ''
      })
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Add Product Config</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Product Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Type *
              </label>
              <select
                {...register('product_type', { 
                  required: 'Product type is required'
                })}
                className="input w-full"
                disabled={isSubmitting}
              >
                <option value="">Select type</option>
                <option value="module">Module</option>
                <option value="roles">Roles</option>
              </select>
              {errors.product_type && (
                <p className="mt-1 text-sm text-red-600">{errors.product_type.message}</p>
              )}
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                API Key *
              </label>
              <input
                {...register('api_key', { 
                  required: 'API Key is required',
                  minLength: { value: 2, message: 'API Key must be at least 2 characters' }
                })}
                type="text"
                className="input w-full"
                placeholder="Enter API Key"
                disabled={isSubmitting}
              />
              {errors.api_key && (
                <p className="mt-1 text-sm text-red-600">{errors.api_key.message}</p>
              )}
            </div>

            {/* API Endpoint */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                API Endpoint *
              </label>
              <input
                {...register('api_endpoint', { 
                  required: 'API Endpoint is required',
                  minLength: { value: 2, message: 'API Endpoint must be at least 2 characters' }
                })}
                type="text"
                className="input w-full"
                placeholder="Enter API Endpoint"
                disabled={isSubmitting}
              />
              {errors.api_endpoint && (
                <p className="mt-1 text-sm text-red-600">{errors.api_endpoint.message}</p>
              )}
            </div>

            {/* HTTP Method */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                HTTP Method *
              </label>
              <select
                {...register('http_method', { 
                  required: 'HTTP Method is required'
                })}
                className="input w-full"
                disabled={isSubmitting}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
              {errors.http_method && (
                <p className="mt-1 text-sm text-red-600">{errors.http_method.message}</p>
              )}
            </div>

            {/* Payload - Only show for POST method */}
            {httpMethod === 'POST' && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Payload (JSON)
                </label>
                <textarea
                  {...register('payload', {
                    validate: (value) => {
                      if (!value || value.trim() === '') return true // Optional field
                      try {
                        JSON.parse(value)
                        return true
                      } catch {
                        return 'Payload must be valid JSON'
                      }
                    }
                  })}
                  className="input w-full min-h-[100px] font-mono text-sm"
                  placeholder='{"key": "value"}'
                  disabled={isSubmitting}
                />
                {errors.payload && (
                  <p className="mt-1 text-sm text-red-600">{errors.payload.message}</p>
                )}
                <p className="mt-1 text-xs text-secondary-500">
                  Optional: JSON payload to send with the request
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[100px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Create Config'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}




